/**
 * Document name cell for the doc-keyed analytics tabs (Intelligent Search,
 * Business Intelligence).
 *
 * The backend only knows the `docId` — the relational `docs.title` column is
 * not reliably denormalized (and many embedded docs have no `docs` row at
 * all), so titles must be resolved frontend-side from the synced Y.Doc via
 * `DocDisplayMetaService`.
 *
 * Docs are mutable: an analyzed document may since have been trashed or hard
 * deleted, yet its cost stays in the billing history. The cell reflects that
 * lifecycle instead of pretending every row is a live, openable doc:
 *  - live doc   → resolved title + open-doc button
 *  - in trash   → resolved title + open-doc button + muted "trash" tag
 *                 (still recoverable, navigation lands in the trash view)
 *  - deleted    → muted "Deleted document" label, no open button (no target);
 *                 the row + cost remain for transparency
 */
import { IconButton } from '@affine/component';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { OpenInNewIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback } from 'react';

export interface DocNameCellProps {
  docId: string;
}

export const DocNameCell = ({ docId }: DocNameCellProps) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const workbench = useService(WorkbenchService).workbench;
  const dialogService = useService(WorkspaceDialogService);

  // doc$ returns the record for live AND trashed docs; null once the doc no
  // longer exists in the workspace (hard deleted / never synced).
  const doc = useLiveData(docsService.list.doc$(docId));
  const trashed = useLiveData(doc?.trash$);
  const title = useLiveData(docDisplayMetaService.title$(docId));

  const openDoc = useCallback(() => {
    // Close the settings dialog before navigating so the doc is visible.
    dialogService.closeAll();
    workbench.openDoc(docId);
  }, [dialogService, workbench, docId]);

  // Deleted: nothing to open — show a neutral label, keep the cost visible.
  if (!doc) {
    return (
      <span style={{ color: 'var(--streamlain-text-secondary-color)' }}>
        {t[
          'com.streamlain.settings.workspace.ai.analytics.deleted-document'
        ]?.() ?? 'Deleted document'}
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
      {trashed && (
        <span
          style={{
            flexShrink: 0,
            fontSize: '11px',
            color: 'var(--streamlain-text-secondary-color)',
          }}
        >
          ·{' '}
          {t['com.streamlain.settings.workspace.ai.analytics.in-trash']?.() ??
            'Trash'}
        </span>
      )}
      <IconButton
        size="16"
        onClick={openDoc}
        tooltip={
          t[
            'com.streamlain.settings.workspace.ai.analytics.open-document'
          ]?.() ?? 'Open document'
        }
      >
        <OpenInNewIcon />
      </IconButton>
    </span>
  );
};
